import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";

Font.register({
  family: "Helvetica-Bold",
  fonts: [{ src: undefined, fontWeight: "bold" }],
});

// ---- PAGE LAYOUT CONSTANTS ----
const PAGE_HEIGHT = 842; // A4 portrait [web:15]
const PAGE_PADDING_TOP_BOTTOM = 32; // 16 top + 16 bottom
const HEADER_BLOCK_HEIGHT = 180; // Header + Invoice + Billing
const FOOTER_IMAGE_HEIGHT = 40; // bottom decorative footer image

const TABLE_HEADER_HEIGHT = 25; // table header
const ROW_HEIGHT = 17; // data row height

// “Heights” in terms of row units for your rules
const TOTALS_MIN_FREE_ROWS = 5; // need 5 row-heights free for totals
const TERMS_MIN_FREE_ROWS = 10; // need 10 row-heights free for terms (without totals)

// Convert your “row-based” rules to point heights
const TOTALS_MIN_FREE_HEIGHT = TOTALS_MIN_FREE_ROWS * ROW_HEIGHT;
const TERMS_MIN_FREE_HEIGHT = TERMS_MIN_FREE_ROWS * ROW_HEIGHT;

// Raw available body height where table + totals + terms sit
const BODY_AVAILABLE_HEIGHT =
  PAGE_HEIGHT -
  PAGE_PADDING_TOP_BOTTOM -
  HEADER_BLOCK_HEIGHT -
  FOOTER_IMAGE_HEIGHT;

// For first page: you want max 25 data rows, no totals/terms on that page
const FIRST_PAGE_MAX_ROWS = 25;

// For “pure data” pages (no totals/terms), available height for rows:
const DATA_ONLY_HEIGHT = BODY_AVAILABLE_HEIGHT - TABLE_HEADER_HEIGHT;
const DATA_ROWS_PER_FULL_PAGE = Math.floor(DATA_ONLY_HEIGHT / ROW_HEIGHT);

const styles = StyleSheet.create({
  page: {
    padding: 16,
    fontSize: 10,
    fontFamily: "Helvetica",
    lineHeight: 1.25,
    position: "relative",
  },
  header: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 8,
    marginBottom: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  logo: {
    width: 100,
    height: "auto",
    marginLeft: "5px",
    objectFit: "contain",
  },
  footerImage: {
    width: "100%",
    height: FOOTER_IMAGE_HEIGHT,
  },
  small: {
    fontSize: 8,
    marginTop: 8,
  },
  billingHeader: {
    backgroundColor: "#fff",
    color: "#000",
    fontSize: 10,
    fontWeight: "bold",
    padding: 4,
    paddingLeft: 6,
  },
  billingRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  billingLabel: {
    width: 80,
    fontSize: 9,
    fontWeight: "bold",
  },
  billingColon: {
    width: 5,
    fontSize: 9,
    textAlign: "center",
  },
  billingValue: {
    flex: 1,
    fontSize: 9,
  },
  table: {
    width: "100%",
    borderWidth: 2,
    borderColor: "#000",
    marginBottom: 8,
  },
  tableHeaderRow: {
    textAlign: "center",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    color: "#000",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  cell: {
    padding: 3,
    borderRightWidth: 1,
    borderRightColor: "#ddd",
  },
  cellSmall: { width: 30 },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#000",
  },
  footerBox: {
    padding: 6,
    width: "48%",
    justifyContent: "center",
  },
  signatureBox: {
    width: "48%",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
  },
  signatureLogo: {
    width: 70,
    height: 40,
    objectFit: "contain",
    marginVertical: 4,
  },
  bankRowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
  },
  qrWrapper: {
    width: 90,
    justifyContent: "center",
    alignItems: "center",
  },
  terms: {
    fontSize: 8,
    marginBottom: 2,
  },
  bankBox: {
    borderWidth: 1,
    borderColor: "#000",
    padding: 6,
    borderRadius: 4,
    marginBottom: 8,
  },
  bankRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  bankLabel: {
    width: 120,
    fontSize: 8,
    marginBottom: 2,
  },
  bankColon: {
    width: 10,
    fontSize: 10,
  },
  bankValue: {
    fontSize: 8,
  },
  qrImage: {
    width: 80,
    height: 80,
    marginTop: 0,
    objectFit: "contain",
  },
  totalSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 6,
    backgroundColor: "#f9f9f9",
    borderTop: "1px solid #ddd",
    borderBottomWidth: 2,
    borderBottomColor: "#000",
    // marginTop: 4,
  },
  totalLeft: {
    flex: 1,
  },
  totalRight: {
    width: 200,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  totalBold: {
    fontWeight: "bold",
  },
  wordsRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 4,
  },
  attendanceHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 6,
    textAlign: "center",
  },
  pageNumber: {
    position: "absolute",
    bottom: 40,
    right: "50%",
    fontSize: 8,
    color: "#666",
  },
});

export default function SprintInvoiceDocument({
  invoice = [],
  detailData = [],
  logoUrl,
  qrUrl,
  totalHours,
  signUrl,
  headerUrl,
  footerUrl,
  data = [],
  withannexure,
  filters = {},
}) {
  const formattedDate = filters.Date
    ? filters.Date.split("-").reverse().join("-")
    : "";

  const header = Array.isArray(invoice) && invoice.length > 0 ? invoice[0] : {};
  const detailsData = Array.isArray(detailData) ? detailData : [detailData];
  const QR_BASE_URL = "https://uaam.beyondexs.com/uploads/images/";

  const qrFullPath = header?.QRCode ? `${QR_BASE_URL}${header.QRCode}` : null;
  const signaturePath = header?.Signature
    ? `${QR_BASE_URL}${header.Signature}`
    : null;
  const headerPath = header?.Header ? `${QR_BASE_URL}${header.Header}` : null;
  const footerPath = header?.Footer ? `${QR_BASE_URL}${header.Footer}` : null;

  const inWords = (num) => {
    num = Number(num);
    if (isNaN(num)) return "Zero";
    if (num === 0) return "Zero";
    num = Math.floor(num);

    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const helper = (n) => {
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " and " + helper(n % 100) : "")
        );
      if (n < 100000)
        return (
          helper(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + helper(n % 1000) : "")
        );
      if (n < 10000000)
        return (
          helper(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 ? " " + helper(n % 100000) : "")
        );
      return (
        helper(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 ? " " + helper(n % 10000000) : "")
      );
    };

    return helper(num);
  };

  const calculateTotals = () => {
    const totalAmount = detailsData.reduce(
      (sum, item) => sum + Number(item.TotalAmount || 0),
      0
    );
    const totalCGST = detailsData.reduce(
      (sum, item) => sum + Number(item.Cgst || 0),
      0
    );
    const totalSGST = detailsData.reduce(
      (sum, item) => sum + Number(item.Sgst || 0),
      0
    );
    const grandTotal = totalAmount;
    return {
      totalAmount: totalAmount.toFixed(2),
      totalCGST: totalCGST.toFixed(2),
      totalSGST: totalSGST.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
    };
  };

  const totals = calculateTotals();

  // ---- PAGINATION PLAN ----

  const totalRows = detailsData.length;

  // 1) First page rows
  const firstPageRows = Math.min(FIRST_PAGE_MAX_ROWS, totalRows);

  // If everything fits into first page (and we allow totals+terms there)
  // we don't enforce your “no totals on first page” rule in that case
  // – if you really NEVER want totals on first page, remove this branch.
  let pagesPlan = [];
  let isFirstPage = true;
  if (totalRows <= FIRST_PAGE_MAX_ROWS) {
    let isOver = true;
    let isUptoTotal = true;
    //terms-only
    if (totalRows <= 10) {
      pagesPlan.push({
        firstpage: isFirstPage,
        type: "data+totals+terms",
        start: 0,
        end: totalRows,
      });
      isFirstPage = false;
    } else if (totalRows <= 20) {
      pagesPlan.push({
        firstpage: isFirstPage,
        type: "data+totals",
        start: 0,
        end: totalRows,
      });
      isOver = false;
      isUptoTotal = true;
      isFirstPage = false;
    } else {
      // Page 1: data only, up to 25 rows
      pagesPlan.push({
        firstpage: isFirstPage,
        type: "data-only",
        start: 0,
        end: firstPageRows,
      });
      isOver = false;
      isUptoTotal = false;
      isFirstPage = false;
    }
    if (!isUptoTotal && !isOver) {
      pagesPlan.push({
        firstpage: isFirstPage,
        type: "total-terms-only",
        start: null,
        end: null,
      });
    }
    if (isUptoTotal && !isOver) {
      pagesPlan.push({
        firstpage: isFirstPage,
        type: "terms-only",
        start: null,
        end: null,
      });
    }
  } else {
    // Page 1: data only, up to 25 rows
    pagesPlan.push({
      firstpage: isFirstPage,
      type: "data-only",
      start: 0,
      end: firstPageRows,
    });
    isFirstPage = false;
    let remainingRows = totalRows - firstPageRows;
    let consumed = firstPageRows;

    // 2) Middle full data pages (no totals, no terms)
    while (remainingRows > 0) {
      // Available height for a possible "totals+terms" page:
      // we need at least 5 rows worth of free space after data for totals,
      // and then 10 rows worth of free space somewhere for terms.
      // To simplify: we keep creating “pure data” pages while remainingRows is larger
      // than what we can safely put on the last data+totals page.
      //
      // Last data page with totals:
      //   - header (TABLE_HEADER_HEIGHT)
      //   - N data rows
      //   - at least TOTALS_MIN_FREE_HEIGHT for totals
      // So: TABLE_HEADER_HEIGHT + N*ROW_HEIGHT + TOTALS_MIN_FREE_HEIGHT <= BODY_AVAILABLE_HEIGHT
      const maxRowsIfLastWithTotals = Math.floor(
        (BODY_AVAILABLE_HEIGHT - TABLE_HEADER_HEIGHT - TOTALS_MIN_FREE_HEIGHT) /
          ROW_HEIGHT
      );

      // If remainingRows <= maxRowsIfLastWithTotals, stop here; this will be last data+totals page.
      if (remainingRows <= maxRowsIfLastWithTotals) {
        // This page will be data+totals (maybe terms as well, depending on space)
        pagesPlan.push({
          firstpage: isFirstPage,
          type: "data+totals-maybe-terms",
          start: consumed,
          end: consumed + remainingRows,
        });
        consumed += remainingRows;
        remainingRows = 0;
      } else {
        // Fill a full data-only page
        const rowsForThisPage = Math.min(
          DATA_ROWS_PER_FULL_PAGE,
          remainingRows
        );
        pagesPlan.push({
          firstpage: isFirstPage,
          type: "data-only",
          start: consumed,
          end: consumed + rowsForThisPage,
        });
        consumed += rowsForThisPage;
        remainingRows -= rowsForThisPage;
      }
      console.log(pagesPlan, "pagesPlan");
    }

    // 3) Decide where terms go: on same page as totals if enough space; else extra page
    const lastIndex = pagesPlan.length - 1;
    if (
      lastIndex >= 0 &&
      pagesPlan[lastIndex].type === "data+totals-maybe-terms"
    ) {
      const lastPageRows =
        pagesPlan[lastIndex].end - pagesPlan[lastIndex].start;

      const usedHeight =
        TABLE_HEADER_HEIGHT +
        lastPageRows * ROW_HEIGHT +
        TOTALS_MIN_FREE_HEIGHT; // approximate totals block

      const freeHeight = BODY_AVAILABLE_HEIGHT - usedHeight;
      console.log();

      if (freeHeight >= TERMS_MIN_FREE_HEIGHT) {
        // terms fit on same page
        pagesPlan[lastIndex].type = "data+totals+terms";
      } else {
        // need separate terms-only page
        pagesPlan[lastIndex].type = "data+totals";
        pagesPlan.push({
          firstpage: isFirstPage,
          type: "terms-only",
          start: null,
          end: null,
        });
      }
    } else if (lastIndex >= 0 && pagesPlan[lastIndex].type === "data-only") {
      // Edge case: if we never marked a totals page (very unlikely with >25 rows),
      // treat last page as data+totals+terms.
      pagesPlan[lastIndex].type = "data+totals+terms";
    }
  }

  const totalPages = pagesPlan.length;

  const renderTotals = () => (
    <View style={styles.totalSection} key="totals">
      <View style={styles.totalLeft}>
        <View style={styles.wordsRow}>
          <Text style={[styles.small, { fontWeight: "bold" }]}>
            Net Total in Words:{" "}
          </Text>
          <Text style={styles.small}>
            {`${inWords(header?.TotalAmount)} Only`}
          </Text>
        </View>
      </View>
      <View style={styles.totalRight}>
        <View style={styles.totalRow}>
          <Text style={styles.small}>Sub-total</Text>
          <Text style={styles.small}>{header?.SubTotal}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={styles.small}>CGST ({header?.Cgst}Amount)</Text>
          <Text style={styles.small}>{header?.TotalCGST}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={styles.small}>SGST ({header?.Sgst}Amount)</Text>
          <Text style={styles.small}>{header?.TotalSGST}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={[styles.small, styles.totalBold]}>Net Total</Text>
          <Text style={[styles.small, styles.totalBold]}>
            {header?.TotalAmount}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderTermsAndBank = (includeSignatureAndBank = true) => (
    <>
      <View style={styles.footerRow}>
        <View style={styles.footerBox}>
          <Text style={[styles.bankLabel, { fontWeight: "bold" }]}>
            Terms and Conditions :
          </Text>
          <Text style={styles.terms}>
            1) Payment can be done through any mode.
          </Text>
          <Text style={styles.terms}>
            2) Any claim for refund is not entertained.
          </Text>
          <Text style={styles.terms}>3) Invoices must be paid in full.</Text>
          <Text style={styles.terms}>
            4) Additional charges may apply for extra services.
          </Text>
          <Text style={styles.terms}>
            5) Services are provided as per the agreement.
          </Text>
          <Text style={styles.terms}>
            6) Partial payments will not be accepted.
          </Text>
        </View>
        {includeSignatureAndBank && (
          <View style={styles.signatureBox}>
            <Text>For {header?.CompanyName}</Text>
            {/* <Text>For Beyondex Solutions Pvt Ltd</Text> */}
            {signaturePath ? (
              <Image src={signaturePath} style={styles.signatureLogo} />
            ) : (
              <View
                style={{ width: 100, height: 25, backgroundColor: "#eee" }}
              />
            )}
            <Text>Authorized Signature</Text>
          </View>
        )}
      </View>

      {includeSignatureAndBank && (
        <View style={styles.bankBox}>
          <View style={styles.bankRowContainer}>
            <View>
              <Text style={[styles.bankLabel, { fontWeight: "bold" }]}>
                Bank Account Details :
              </Text>
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Bank Name</Text>
                <Text style={styles.bankColon}>:</Text>
                <Text style={styles.bankValue}>{header?.BankName}</Text>
              </View>
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Account Name</Text>
                <Text style={styles.bankColon}>:</Text>
                <Text style={styles.bankValue}>{header?.BankAccountName}</Text>
                {/* <Text style={styles.bankValue}>Beyondex Solutions Pvt Ltd</Text> */}
              </View>
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Account Number</Text>
                <Text style={styles.bankColon}>:</Text>
                <Text style={styles.bankValue}>{header?.AccountNo}</Text>
              </View>
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>IFSC Code</Text>
                <Text style={styles.bankColon}>:</Text>
                <Text style={styles.bankValue}>{header?.IFSCCode}</Text>
              </View>
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Branch</Text>
                <Text style={styles.bankColon}>:</Text>
                <Text style={styles.bankValue}>{header?.Branch}</Text>
              </View>
            </View>
            <View style={styles.qrWrapper}>
              {qrFullPath ? (
                <Image src={qrFullPath} style={styles.qrImage} />
              ) : (
                <View
                  style={{ width: 80, height: 80, backgroundColor: "#eee" }}
                />
              )}
            </View>
          </View>
        </View>
      )}
    </>
  );

  const renderTableRows = (start, end) => {
    const rows = [];
    for (let i = start; i < end; i++) {
      const it = detailsData[i];
      rows.push(
        <View style={styles.tableRow} key={`row-${i}`}>
          <Text style={[styles.cell, styles.cellSmall]}>{it.SLNO}</Text>
          <Text style={[styles.cell, { width: 180 }]}>{it.Product}</Text>
          <Text style={[styles.cell, { width: 60 }]}>{it.Unit}</Text>
          <Text style={[styles.cell, { width: 60, textAlign: "right" }]}>
            {it.Cgst}%
          </Text>
          <Text style={[styles.cell, { width: 60, textAlign: "right" }]}>
            {it.Sgst}%
          </Text>
          <Text style={[styles.cell, { width: 80, textAlign: "right" }]}>
            {it.Quantity}
          </Text>
          <Text style={[styles.cell, { width: 80, textAlign: "right" }]}>
            {it.Price}
          </Text>
          <Text style={[styles.cell, { width: 80, textAlign: "right" }]}>
            {it.Amount}
          </Text>
        </View>
      );
    }
    return rows;
  };

  const renderBillingHeader = (isFirstPage) => (
    <>
      <View style={styles.header}>
        {headerPath ? (
          <Image src={headerPath} style={styles.logo} />
        ) : (
          <View
            style={{
              width: "200%",
              height: 60,
              backgroundColor: "#eee",
              marginRight: 8,
            }}
          />
        )}
      </View>

      <Text style={styles.attendanceHeader}>Invoice</Text>

      <View style={{ marginBottom: 12 }}>
        <Text style={styles.billingHeader}>Billing To:</Text>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            padding: 6,
          }}
        >
          <View style={{ flex: 1, marginRight: 20 }}>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Party Name</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.CustomerName || ""}
              </Text>
            </View>
            {isFirstPage &&(<>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Address</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.CustomerAddress || ""}
              </Text>
            </View>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>GST No</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.CustomerGstNo || ""}
              </Text>
            </View>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Reference</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.ProjectName || ""}
              </Text>
            </View>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Accountable Mgr</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.AccountableMgr || ""}
              </Text>
            </View>
            </>
            )}
          </View>
          <View style={{ width: 220 }}>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Invoice No</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.InvoiceNumber || ""}
              </Text>
            </View>
            {isFirstPage &&(<>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Invoice Date</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.InvoiceDate || ""}
              </Text>
            </View>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Mobile</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.CustomerMobileNo || ""}
              </Text>
            </View>
            <View style={styles.billingRow}>
              <Text style={styles.billingLabel}>Email</Text>
              <Text style={styles.billingColon}>:</Text>
              <Text style={styles.billingValue}>
                {header?.CustomerMailID || ""}
              </Text>
            </View>
            </>)}
          </View>
        </View>
      </View>
    </>
  );

  const renderPageContent = (pageIndex) => {
    const pagePlan = pagesPlan[pageIndex];

    return (
      <>
        {renderBillingHeader(pagePlan.firstpage)}

        {pagePlan.type !== "terms-only" &&
          pagePlan.type !== "total-terms-only" && (
            <View style={styles.table}>
              <View style={styles.tableHeaderRow}>
                <Text
                  style={[
                    styles.cell,
                    styles.cellSmall,
                    { fontWeight: "bold" },
                  ]}
                >
                  SL#
                </Text>
                <Text style={[styles.cell, { width: 180, fontWeight: "bold" }]}>
                  Description
                </Text>
                <Text style={[styles.cell, { width: 60, fontWeight: "bold" }]}>
                  Unit
                </Text>
                <Text style={[styles.cell, { width: 60, fontWeight: "bold" }]}>
                  CGST
                </Text>
                <Text style={[styles.cell, { width: 60, fontWeight: "bold" }]}>
                  SGST
                </Text>
                <Text style={[styles.cell, { width: 80, fontWeight: "bold" }]}>
                  Qty
                </Text>
                <Text style={[styles.cell, { width: 80, fontWeight: "bold" }]}>
                  Rate
                </Text>
                <Text style={[styles.cell, { width: 80, fontWeight: "bold" }]}>
                  Total
                </Text>
              </View>
              {renderTableRows(pagePlan.start ?? 0, pagePlan.end ?? 0)}
            </View>
          )}

        {(pagePlan.type === "data+totals" ||
          pagePlan.type == "total-terms-only" ||
          pagePlan.type === "data+totals+terms") &&
          renderTotals()}

        {(pagePlan.type === "data+totals+terms" ||
          pagePlan.type == "total-terms-only") &&
          renderTermsAndBank(true)}

        {(pagePlan.type === "terms-only" ||
          pagePlan.type == "total-terms-only") &&
          renderTermsAndBank(true)}

        <Text style={styles.pageNumber}>
          Page {pageIndex + 1} of {totalPages}
        </Text>

        {footerPath ? (
          <Image
            src={footerPath}
            style={[
              styles.footerImage,
              { position: "absolute", bottom: 0, left: 0, right: 0 },
            ]}
          />
        ) : (
          <View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: FOOTER_IMAGE_HEIGHT,
              backgroundColor: "#eee",
            }}
          />
        )}
      </>
    );
  };

  return (
    <Document>
      {pagesPlan.map((_, pageIndex) => (
        <Page key={`page-${pageIndex}`} size="A4" style={styles.page}>
          {renderPageContent(pageIndex)}
        </Page>
      ))}
    </Document>
  );
}
